import React, { useEffect, useState } from "react";
import {
  Avatar,
  CssBaseline,
  Box,
  Typography,
  Container,
  Alert,
  Button,
  Stack,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { observer } from "mobx-react";
import { useForm } from "react-hook-form";
import TextComponent from "../../components/text/Text";
import { errorGenerator } from "../../utils/utils";
import { ILogin } from "../../interfaces/ILogin";
import rootStores from "../../stores";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const authStore = rootStores.AuthStore;

const LoginComponent = observer(() => {
  const { t, i18n } = useTranslation();
  const { login } = authStore;
  const { handleSubmit, control, watch } = useForm();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async ({ email, password }: ILogin) => {
    setLoading(true);
    const res = await login({ email, password });
    setLoading(false);

    if (res?.status !== 200) {
      if (res?.includes("401")) {
        setError("401Login");
      }
    } else {
      navigate("/TicketsCompare");
    }
  };

  const changeLanguage = (lng: "he" | "en") => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
    document.body.dir = lng === "he" ? "ltr" : "rtl";
  };

  useEffect(() => {
    const savedLang = (localStorage.getItem("language") as "he" | "en") || "en";
    i18n.changeLanguage(savedLang);
    document.body.dir = savedLang === "he" ? "ltr" : "rtl";
  }, [i18n]);

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />

      {/* Language Switch */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}>
        <Button size="small" onClick={() => changeLanguage("he")}>
          עברית
        </Button>
        <Button size="small" onClick={() => changeLanguage("en")}>
          English
        </Button>
      </Stack>

      <Box
        sx={{
          marginTop: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component="h1" variant="h5">
          {t("login.title")}
        </Typography>

        <Box sx={{ mt: 3, minWidth: 300 }}>
          <form onSubmit={handleSubmit((data) => onSubmit(data as ILogin))} noValidate>
            <TextComponent
              control={control}
              registerName="email"
              labelText={t("login.email")}
              isRequired
              type="email"
              isFullWidth
              helperTextError={t("login.emailError")}
              style={{ marginTop: "5%" }}
            />

            <TextComponent
              isPassword
              control={control}
              registerName="password"
              labelText={t("login.password")}
              isRequired
              type="password"
              isFullWidth
              helperTextError={t("login.passwordError")}
              style={{ marginTop: "5%" }}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorGenerator(error)}
              </Alert>
            )}

            <LoadingButton
              type="submit"
              fullWidth
              loading={loading}
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={!watch("email") || !watch("password")}
            >
              {t("login.submit")}
            </LoadingButton>
          </form>
        </Box>
      </Box>
    </Container>
  );
});

export default LoginComponent;
